import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetScrollView
} from '@gorhom/bottom-sheet';
import type { FC, Ref } from 'react';
import { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { TutorialView } from '@/features/tutorial/components/TutorialView';
import { useTutorial } from '@/features/tutorial/providers/useTutorial';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    background: {
      backgroundColor: theme.colors.surfaceContainerLow
    },
    view: {
      paddingBottom: insets.bottom
    }
  });

type Props = {
  ref: Ref<BottomSheetModal | null>;
};

export const TutorialSheet: FC<Props> = ({ ref }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [theme, insets]);
  const { dismiss, markCompleted } = useTutorial();
  // Finishing the tour keeps the "first steps" card on the start screen but
  // moves it to the end of the card carousel (see StartCardCarousel).
  const finish = useCallback(() => {
    markCompleted();
    dismiss();
  }, [markCompleted, dismiss]);

  return (
    <BottomSheetModal
      ref={ref}
      name="tutorial"
      handleComponent={() => <></>}
      backgroundStyle={styles.background}
      enableDynamicSizing={false}
      snapPoints={['90%']}
      enableHandlePanningGesture={false}
      enableOverDrag={false}
      enableContentPanningGesture={false}
      backdropComponent={(props) => (
        <BottomSheetBackdrop
          {...props}
          disappearsOnIndex={-1}
          appearsOnIndex={0}
          opacity={0.5}
          pressBehavior="close"
        />
      )}
    >
      <BottomSheetScrollView contentContainerStyle={styles.view}>
        <TutorialView onClose={dismiss} onFinish={finish} />
      </BottomSheetScrollView>
    </BottomSheetModal>
  );
};
